import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import ConfirmationDialog from '@/components/ui/ConfirmationDialog/ConfirmationDialog';
import { PaymentDeviceDtoIn, PaymentDeviceRentalRequestDtoIn } from '@/models/in/PaymentDeviceDtoIn';
import { MANAGEMENT_DEVICES_URL, MANAGEMENT_DEVICE_REQUESTS_URL, PaymentDeviceAction, paymentDeviceErrorMessage } from '@/helpers/paymentDevices';
import usePaymentDeviceApi from './usePaymentDeviceApi';
import DeviceFormDialog from './dialogs/DeviceFormDialog';
import AssignDeviceDialog from './dialogs/AssignDeviceDialog';
import DeviceActionDialog, { DeviceActionTarget, DeviceStatusAction } from './dialogs/DeviceActionDialog';
import ApproveRequestDialog from './dialogs/ApproveRequestDialog';
import HandOverRequestDialog from './dialogs/HandOverRequestDialog';
import RejectRequestDialog from './dialogs/RejectRequestDialog';

interface AssignState {
  device: PaymentDeviceDtoIn | null;
  fundId: number | null;
  fundName?: string;
}

/**
 * The device dialogs, shared by the inventory, the device blade and the fund blade's tab: each
 * screen calls run(action, device) and renders `dialogs` once.
 */
export const useDeviceDialogs = () => {
  const { t } = useTranslation();
  const api = usePaymentDeviceApi();

  const [formDevice, setFormDevice] = useState<PaymentDeviceDtoIn | null | undefined>(undefined);
  const [assign, setAssign] = useState<AssignState | null>(null);
  const [statusAction, setStatusAction] = useState<{ action: DeviceStatusAction; target: DeviceActionTarget } | null>(null);
  const [toDelete, setToDelete] = useState<PaymentDeviceDtoIn | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const openCreate = () => setFormDevice(null);
  const openAssign = (state: AssignState) => setAssign(state);
  const runOnTarget = (action: DeviceStatusAction, target: DeviceActionTarget) => setStatusAction({ action, target });

  const run = (action: PaymentDeviceAction, device: PaymentDeviceDtoIn) => {
    switch (action) {
      case 'edit':
        setFormDevice(device);
        return;
      case 'assign':
        setAssign({ device, fundId: null });
        return;
      case 'delete':
        setDeleteError(null);
        setToDelete(device);
        return;
      default:
        runOnTarget(action, { deviceId: device.id, deviceNumber: device.deviceNumber, rental: device.currentRental });
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    setDeleteError(null);
    setIsDeleting(true);
    try {
      await api.del(`${MANAGEMENT_DEVICES_URL}/${toDelete.id}`);
      setToDelete(null);
    } catch (err) {
      setDeleteError(paymentDeviceErrorMessage(err, t, 'paymentDevices.errors.save'));
    } finally {
      setIsDeleting(false);
    }
  };

  const dialogs = (
    <>
      <DeviceFormDialog open={formDevice !== undefined} device={formDevice ?? null} onClose={() => setFormDevice(undefined)} />
      <AssignDeviceDialog
        open={assign !== null}
        device={assign?.device ?? null}
        fundId={assign?.fundId ?? null}
        fundName={assign?.fundName}
        onClose={() => setAssign(null)}
      />
      <DeviceActionDialog action={statusAction?.action ?? null} target={statusAction?.target ?? null} onClose={() => setStatusAction(null)} />
      <ConfirmationDialog
        open={toDelete !== null}
        onOpenChange={(isOpen) => { if (!isOpen) setToDelete(null); }}
        title={t('paymentDevices.dialogs.deleteTitle')}
        message={[t('paymentDevices.dialogs.deleteConfirm', { number: toDelete?.deviceNumber ?? '' }), deleteError].filter(Boolean).join('\n\n')}
        confirmText="paymentDevices.dialogs.deleteConfirmButton"
        cancelText="paymentDevices.dialogs.cancel"
        variant="danger"
        isLoading={isDeleting}
        onConfirm={confirmDelete}
      />
    </>
  );

  return { run, runOnTarget, openCreate, openAssign, dialogs };
};

/**
 * The dialogs of rental requests - approve, reject, hand the held device over, and bring a cancelled
 * request back to pending - shared by the request tabs and the fund blade's tab.
 */
export const useRequestDialogs = (onOpenRequest?: (requestId: number) => void) => {
  const { t } = useTranslation();
  const api = usePaymentDeviceApi();
  const [toApprove, setToApprove] = useState<PaymentDeviceRentalRequestDtoIn | null>(null);
  const [toReject, setToReject] = useState<PaymentDeviceRentalRequestDtoIn | null>(null);
  const [toCollect, setToCollect] = useState<PaymentDeviceRentalRequestDtoIn | null>(null);
  const [toReopen, setToReopen] = useState<PaymentDeviceRentalRequestDtoIn | null>(null);
  const [reopenError, setReopenError] = useState<string | null>(null);
  const [isReopening, setIsReopening] = useState(false);

  const reopen = (request: PaymentDeviceRentalRequestDtoIn) => {
    setReopenError(null);
    setToReopen(request);
  };

  const confirmReopen = async () => {
    if (!toReopen) return;
    setReopenError(null);
    setIsReopening(true);
    try {
      await api.post(`${MANAGEMENT_DEVICE_REQUESTS_URL}/${toReopen.id}/reopen`, {});
      setToReopen(null);
    } catch (err) {
      setReopenError(paymentDeviceErrorMessage(err, t, 'paymentDevices.errors.save'));
    } finally {
      setIsReopening(false);
    }
  };

  const dialogs = (
    <>
      <ApproveRequestDialog
        request={toApprove}
        onClose={() => setToApprove(null)}
        onRejectNoDevices={(request) => { setToApprove(null); setToReject(request); }}
        onOpenHeldRequest={onOpenRequest && ((requestId) => { setToApprove(null); onOpenRequest(requestId); })}
      />
      <RejectRequestDialog request={toReject} onClose={() => setToReject(null)} />
      <HandOverRequestDialog request={toCollect} onClose={() => setToCollect(null)} />
      <ConfirmationDialog
        open={toReopen !== null}
        onOpenChange={(isOpen) => { if (!isOpen) setToReopen(null); }}
        title={t('paymentDevices.dialogs.reopenTitle', { id: toReopen?.id ?? '' })}
        message={[t('paymentDevices.dialogs.reopenConfirm'), reopenError].filter(Boolean).join('\n\n')}
        confirmText="paymentDevices.actions.reopen"
        cancelText="paymentDevices.dialogs.cancel"
        isLoading={isReopening}
        onConfirm={confirmReopen}
      />
    </>
  );

  return { approve: setToApprove, reject: setToReject, collect: setToCollect, reopen, dialogs };
};
